import { apolloClient } from '@/lib/apollo';
import { GET_SESSION_USER_INFO } from '@/graphql/user/queries';
import { getAccessToken } from './tokenManager';
import { refreshAccessToken } from './refreshToken';
import { logger } from './logger';

interface UserSessionInfo {
  id: string;
  account_id?: string;
  email: string;
  firstName: string;
  lastName: string;
  avatarUrl?: string;
  locale?: string;
  role?: string;
  phone?: string;
  timeZone?: string;
  isWelcomeScreenClosed?: boolean;
  isEmailConfirmed?: boolean;
  settings?: {
    dashboardSettings?: {
      showTimer?: boolean;
      showClock?: boolean;
      showDate?: boolean;
      showSeconds?: boolean;
      upcomingJobsWindow?: number;
      dashboardMaxListLength?: number;
      dashboardShowOnlyNew?: boolean;
      timeFormat?: string;
    };
  };
}

interface ValidationResult {
  success: boolean;
  error?: string;
  errorCode?: string;
  user?: UserSessionInfo;
  isNetworkError?: boolean;
}

/**
 * Attempts to validate the session with the given token
 */
const attemptValidation = async (token: string): Promise<ValidationResult> => {
  try {
    const { data } = await apolloClient.query({
      query: GET_SESSION_USER_INFO,
      context: {
        headers: {
          authorization: `Bearer ${token}`,
        },
        // Mark this to prevent errorLink from handling auth errors during validation
        skipRefresh: true,
      },
      fetchPolicy: 'network-only', // Always fetch fresh data
      errorPolicy: 'all',
    });

    // Check if we have a valid response with user data
    if (data?.getSessionUserInfo?.__typename === 'GetUserSession' && data.getSessionUserInfo.user) {
      return {
        success: true,
        user: data.getSessionUserInfo.user,
      };
    } else if (data?.getSessionUserInfo?.__typename === 'Error') {
      const errorCode = data.getSessionUserInfo.errorCode;
      const isUnauthorized = errorCode === '401' || errorCode === 401;
      return {
        success: false,
        error: isUnauthorized
          ? 'Unauthorized'
          : data.getSessionUserInfo.message || 'Session validation failed',
      };
    } else {
      return {
        success: false,
        error: 'No user session data returned',
      };
    }
  } catch (error) {
    // Check if this is a network error
    const isNetworkError =
      error instanceof Error &&
      (error.message.includes('Failed to fetch') ||
        error.message.includes('Network') ||
        error.message.includes('timeout'));

    logger.error({
      message: 'Session validation failed',
      context: { error: error instanceof Error ? error.message : 'Unknown error', isNetworkError },
    });

    return {
      success: false,
      error: error instanceof Error ? error.message : 'Session validation failed',
      isNetworkError,
    };
  }
};

/**
 * Validates the current user session with automatic token refresh on failure
 * @param token Optional access token to validate. If not provided, uses token from storage.
 * @returns Promise that resolves with validation result
 */
export const validateSession = async (token?: string): Promise<ValidationResult> => {
  const accessToken = token || getAccessToken();

  if (!accessToken) {
    return { success: false, error: 'No access token available' };
  }

  // First attempt at validation
  const firstAttempt = await attemptValidation(accessToken);

  // If successful or network error, return immediately
  if (firstAttempt.success || firstAttempt.isNetworkError) {
    return firstAttempt;
  }

  // If validation failed with unauthorized, try refreshing token and retry
  if (!firstAttempt.success && firstAttempt.error === 'Unauthorized') {
    logger.info('[VALIDATE_SESSION] Unauthorized, attempting token refresh...');

    // Attempt to refresh the token
    const refreshResult = await refreshAccessToken();

    if (refreshResult.success && refreshResult.newToken) {
      logger.info('[VALIDATE_SESSION] Token refreshed, retrying validation...');
      // Retry validation with new token
      const retryAttempt = await attemptValidation(refreshResult.newToken);
      if (retryAttempt.success) {
        return retryAttempt;
      }
      logger.warn('[VALIDATE_SESSION] Retry validation failed after token refresh');
    } else {
      logger.warn('[VALIDATE_SESSION] Token refresh failed', { error: refreshResult.error });
      // Propagate errorCode (e.g. SESSION_EVICTED) so caller can show the re-auth modal
      // instead of redirecting to the sign-in page
      if (refreshResult.errorCode) {
        return {
          success: false,
          error: refreshResult.error || 'Token refresh failed',
          errorCode: refreshResult.errorCode,
        };
      }
    }
  }

  return firstAttempt;
};
